export const ADMIN_ROLES = [
    'SUPER_ADMIN',
    'COUNTRY_ADMIN',
    'CITY_ADMIN',
    'OPS',
    'SUPPORT',
    'SAFETY',
    'FINANCE',
    'MARKETING',
    'DRIVER_OPS',
    'MERCHANT_OPS',
    'ANALYST',
    'READ_ONLY',
    // Persona roles (D355): a store owner/manager (lojista), a fleet manager (frota), a partner-desk agent and a
    // support agent (atendente). Each is bounded by its own scope kind — see ROLE_SCOPE_TYPES.
    'MERCHANT_MANAGER',
    'FLEET_MANAGER',
    // D365 (ADR-0047): renamed from `PDV_OPERATOR`. The owner's decision D363 #2 settled what the "PDV" is — a
    // **partner desk** (a hotel, inn or company ordering for a guest), not a store till — so the role is bounded by
    // one PARTNER account, never by a merchant.
    'PARTNER_AGENT',
    'SERVICE_AGENT',
];
/**
 * How a grant is scoped (mirrors `admin_role_grants.scope_type`): platform-wide, a country (`cc`), a state — the ISO 3166-2
 * first-level administrative region (`cc-XX`, ADR-0124), a single city/market (`cc:slug`), one merchant (its merchant id), one
 * fleet organization (its organization id) or one partner account (its account id, D365).
 */
export const SCOPE_TYPES = ['GLOBAL', 'COUNTRY', 'REGION', 'CITY', 'MERCHANT', 'FLEET', 'PARTNER'];
/** Actions that only read; everything else is a privileged (mutating/sensitive) action. */
export const READ_ACTIONS = ['view', 'list'];
/**
 * Role → granted permissions (data). SUPER/COUNTRY/CITY admins hold broad grants bounded by their scope on
 * assignment; functional roles hold a focused set; READ_ONLY may only read. Persona roles hold only the words whose
 * routes enforce their merchant/fleet/partner/market scope — a persona word on a platform-scoped route can never be
 * reached, because a MERCHANT, FLEET, PARTNER or CITY grant does not cover a platform target. One recorded exception
 * (D419): the atendente holds `order:view` / `delivery:view` by owner decision D408 #7 ahead of their routes taking a market.
 */
export const ROLE_PERMISSIONS = {
    SUPER_ADMIN: ['*:*'],
    COUNTRY_ADMIN: ['*:*'],
    // D854 (L6c-F06; doc 17 §3.1, ADR-0102 #7): the owner's deposit into the incentive fund is a maker/checker pair of FINANCE
    // people. A city administrator's `*:*` covered both halves, so two city admins could create fund money with no FINANCE
    // operator; the city admin keeps reading the fund (`incentive_fund:view`) and never invests nor approves.
    CITY_ADMIN: ['*:*', '!incentive_fund:invest', '!incentive_fund:approve'],
    OPS: [
        // D520: the platform BI (counts, rates, money per currency) of the markets the grant covers — the OPS managers already read
        // the live dashboard (`ops:view`) at the same scopes. Never the names (`analytics:view_personal`) nor the CSV (`analytics:export`).
        'analytics:view', 'analytics:save_report',
        'ride:view', 'ride:list', 'ride:cancel', 'ride:cancel_batch', 'driver:view', 'dispatch:view', 'dispatch:override', 'ops:view', 'account:view',
        'delivery:view', 'delivery:create', 'delivery:manage', 'claim:view', 'claim:manage', 'claim:decide',
        'courier:view', 'courier:manage', 'food_ops:view', 'food_ops:manage', 'order:view', 'order:manage',
        'postsale:view', 'postsale:manage', 'postsale:decide', 'dispute:view', 'dispute:decide', 'forecast:view',
        // D397: OPS is the role that acts as the post-sale supervisor (it already held `postsale:decide`, whose override
        // declared `by: supervisor`); the elevated role is now this word, never a body field. SUPPORT keeps manage only.
        'postsale:override',
        'sc_governance:view', 'sc_governance:manage', 'conversation:view',
        // D378: read a market's courier jobs and release a stuck courier (reason-coded, audited).
        'courier_job:view', 'courier_job:release',
        // D474: the alert center (the operators read what the platform raised and mark it read).
        'alert:view', 'alert:manage',
        // D473: the central speaks in a ride's chat and takes ride orders by phone.
        'ride_chat:read', 'ride_chat:send', 'central_order:view', 'central_order:create',
        // D478: the occurrences riders and drivers report, and the relationships console.
        'occurrence:read', 'occurrence:respond', 'relationship:view',
        // D674: the shared places base - the operations managers read the address list and curate it (create, edit, hide, delete) inside their geography;
        // the bulk clear (`places:purge`) stays with the admin roles.
        'places:view', 'places:manage',
    ],
    SUPPORT: [
        'ride:view', 'rider:view', 'driver:view', 'support:view', 'support:respond', 'account:view',
        'conversation:view', 'conversation:manage', 'conversation:read_body', 'kb:view', 'agent_desk:view', 'agent_desk:assign', 'agent_desk:supervise',
        // D632: the Central teams (read) - the supervisor sees who works where; managing them is the admins'.
        'central_team:view',
        'voice:view', 'voice:manage',
        'dispute:view', 'postsale:view', 'postsale:manage', 'loyalty:view', 'claim:view', 'order:view',
        // D384 review (W5S-04, ADR-0054 §1): the staff support role works the ticket desk under the atendente's rules — the
        // ticket detail now holds every ticket action, and the full phone is still only a logged reveal on a ticket assigned
        // to the caller (decision D381 #3).
        'support_ticket:view', 'support_ticket:manage', 'support_contact:reveal',
        // D459 (ADR-0074): a driver asking why he gets no offers — support reads his wallet standing and statement.
        'wallet:view',
        // D737a (ADR-0150): support reads a rider's balance and proposes a credit/debit (a second operator approves one above the threshold).
        'rider_wallet:view', 'rider_wallet:propose',
        'alert:view', 'alert:manage',
        // D473: the support agents are the central's attendants — chat and phone orders.
        'ride_chat:read', 'ride_chat:send', 'central_order:view', 'central_order:create',
        // D478: they answer the occurrences and read the relationships console.
        'occurrence:read', 'occurrence:respond', 'relationship:view',
    ],
    SAFETY: [
        'safety:view', 'safety:manage', 'ride:view', 'driver:view', 'driver:suspend', 'rider:suspend', 'account:view',
        'safety_evidence:view', 'safety_evidence:manage', 'safety_evidence:access', 'safety_evidence:legal_hold', 'safety_evidence:delete',
        'telematics:view',
        'alert:view', 'alert:manage',
        // D473: the safety team reads a ride's chat history (it judges an incident on it) but never speaks in it.
        'ride_chat:read',
        // D478: it reads the occurrences and the blacklist (a block with a safety reason is its business) and answers an occurrence.
        'occurrence:read', 'occurrence:respond', 'relationship:view',
        // H1-RULES (D503, owner Q-136): the safety team is the one that undoes a block on the person's behalf (audited, a reason is required).
        'relationship:unblock',
    ],
    FINANCE: [
        // D385 (ADR-0055): `payout:approve` is the driver payout's checker word, replacing `payout:hold` on the decision (no
        // route checks `hold` any more, so FINANCE no longer holds it). FINANCE holds both halves; four eyes is the identity
        // check (two different FINANCE people), never the word — `SELF_APPROVAL` is refused even under `*:*`.
        // D520: the financial indicators of the BI (gross, commission, take rate, partner spend) — money per currency.
        'analytics:view', 'analytics:save_report',
        'payment:view', 'payment:refund', 'payment:chargeback', 'payout:view', 'payout:create', 'payout:approve', 'payout:settle', 'ledger:view',
        'settlement:view', 'settlement:run', 'dispute:view', 'dispute:decide',
        // D368: the partner account's bill — close a period early, record a payment (maker), approve it (checker).
        'partner_billing:view', 'partner_billing:close', 'partner_billing:record', 'partner_billing:approve',
        // D398 (ADR-0063): the refund/credit queue — FINANCE reads it and is its checker (never of its own items); a credit to
        // a partner is a credit note FINANCE proposes on a closed statement (the maker).
        'refund:view', 'refund:approve', 'partner_billing:credit',
        // D459 (ADR-0074): the prepaid wallet — packages, adjustments and ceiling overrides (maker and checker are two people).
        'wallet:view', 'wallet:manage', 'wallet:approve', 'driver_group:manage',
        // D737a (ADR-0150): the rider's balance — read, propose and approve (maker and checker are two people).
        'rider_wallet:view', 'rider_wallet:propose', 'rider_wallet:approve',
        // D489 (ADR-0102): the incentive fund — the owner's deposits are a maker/checker pair of FINANCE people.
        'incentive_fund:view', 'incentive_fund:invest', 'incentive_fund:approve',
        // D854 (L6c-F08): the zones the incentive cockpit, the offers and the zone map draw (a read; proposing a zone stays with the admins).
        'incentive_zone:view',
        // D490 (ADR-0106): the lever offers - a maker/checker pair of FINANCE people, and either may kill one.
        'incentive_lever:view', 'incentive_lever:manage', 'incentive_lever:approve',
        // D493 (ADR-0109): the targeted coupon campaigns - a maker/checker pair of FINANCE people (approval reserves the budget), and either may kill one.
        'coupon_campaign:view', 'coupon_campaign:manage', 'coupon_campaign:approve',
        'alert:view', 'alert:manage',
        // D477: the award campaign's second pair of eyes (it pays money into driver wallets).
        'award:view', 'award:manage',
        // D639: the AI spend panel (read) - finance watches what the Central's AI costs against its budget.
        'ai_spend:view',
    ],
    MARKETING: [
        'promotion:view', 'promotion:publish', 'campaign:view', 'campaign:manage', 'pricing:view', 'ads:view', 'ads:manage',
        'loyalty:view', 'loyalty:manage',
        // D477 (ADR-0095): the mass broadcast, the banners and the driver award campaigns (a second operator activates an award).
        'broadcast:view', 'broadcast:send', 'banner:view', 'banner:manage', 'award:view', 'award:manage',
    ],
    DRIVER_OPS: [
        'driver:view', 'driver:approve', 'driver:suspend', 'driver:register', 'document:view', 'document:review', 'vehicle:view', 'vehicle:manage',
        'fleet:view', 'fleet:manage', 'fleet:onboard', 'fleet:approve', 'fleet:contract', 'driver_loyalty:view', 'driver_loyalty:manage',
        'telematics:view', 'telematics:manage', 'courier:view',
        // D459 (ADR-0074): driver groups, and a read of a driver's wallet standing (why he is not offered work).
        'driver_group:manage', 'wallet:view',
        // D477: what the award campaigns pay his drivers (read only).
        'award:view',
    ],
    MERCHANT_OPS: [
        'merchant:view', 'merchant:manage', 'merchant:onboard', 'merchant:approve', 'merchant_review:view', 'merchant_review:submit',
        'merchant_review:decide', 'merchant_staff:view', 'merchant_staff:manage', 'store:view', 'store:operate', 'store:manage',
        // D394 (owner decision D391 #6, "only the merchant edits its catalog"): platform merchant ops READ a catalog; the
        // catalog words that write it (`catalog:publish`, `catalog:availability`) are the merchant's own.
        'store:service_area', 'catalog:view', 'order:view', 'promotion:view',
    ],
    // D523: the analyst keeps its own saved BI reports (a personal list, not a platform change); READ_ONLY keeps none.
    ANALYST: ['*:view', 'report:view', 'report:export', 'analytics:save_report'],
    READ_ONLY: ['*:view'],
    // Lojista: runs its own merchant — its stores, orders, promotions, staff and statements. Never onboarding, payout
    // accounts, approvals or platform money. D394 (ADR-0061): and its OWN catalog — every catalog entity now records an
    // immutable merchant owner, so the catalog words reach only the entities its MERCHANT grant covers (text only: name,
    // description, price, sold-out).
    MERCHANT_MANAGER: [
        'merchant:view', 'merchant:manage', 'merchant_staff:view', 'merchant_staff:manage', 'store:view', 'store:operate', 'store:manage',
        'order:view', 'order:create', 'order:manage', 'promotion:view', 'promotion:publish', 'settlement:view', 'payout:view', 'ads:view',
        'catalog:view', 'catalog:publish', 'catalog:availability',
    ],
    // Frota: runs its own fleet organization — assets, driver attachments, assignments and statements. Never the
    // organization lifecycle or contracts.
    FLEET_MANAGER: ['fleet:view', 'fleet:manage', 'settlement:view'],
    // Partner desk (D365): a receptionist of ONE partner account — reads its account (markets, limit, usage) and
    // orders, reads and cancels rides for its guests. Never another account, never a merchant, never platform money.
    // D366: and the same for a delivery and a food order for its guests (never a merchant's or a courier's action).
    PARTNER_AGENT: [
        'partner:view',
        'partner_ride:create', 'partner_ride:view', 'partner_ride:cancel',
        'partner_delivery:create', 'partner_delivery:view', 'partner_delivery:cancel',
        'partner_food:create', 'partner_food:view', 'partner_food:cancel',
        // D368: the account's statements. The word only lets the desk TRY; the membership must be an owner or a manager
        // (`billing:view` in the business kernel) — a plain receptionist is refused.
        'partner_billing:view',
    ],
    // Atendente: support inside its city or country — reads rides and accounts, works tickets, cancels with a reason.
    // D378 (ADR-0051): the Admin is the pilot support console (D353), so the atendente also reads its market's courier jobs
    // and releases a stuck courier there with a reason code — the narrowest words that do it (never `dispatch:*`).
    // D384 (ADR-0054): the ticket desk — the ticket and its minimal subject card, the work on a ticket assigned to itself,
    // and the logged full-phone reveal. Never `rider:view` / `driver:view` (the broad directory reads).
    // D419 (owner decision D408 #7): the NARROW view words the live desk and the D401 screens read.
    //   - `conversation:view` + `agent_desk:view`: the live Central desk (the queue, a conversation, who owns it). Since
    //     D419 those routes authorize at the conversation's market, so a CITY/COUNTRY grant reaches its own.
    //   - D419 owner decision (2026-09-24): the pilot atendente WORKS the Central at its market — `conversation:manage`
    //     (open, reply, note, link, transition) and `agent_desk:assign` (claim; release or hand over what it OWNS). It never
    //     holds `agent_desk:supervise` (taking over a colleague's conversation), and the server lets only a conversation's
    //     owner write to it (review W7C-01).
    //   - `order:view` + `delivery:view`: the D401 "where is my order / my parcel" reads (the D401 handoff's narrow
    //     proposal). Their routes are still MERCHANT/PLATFORM targets (`GET /v1/food/orders`, `GET /v1/deliveries`), which a
    //     geographic grant never covers, so they take effect only when those routes authorize at a market (wave 8).
    //     `claim:view` / `postsale:view` (decision queues), `store:view` and `settlement:view` (merchant and finance) are
    //     deliberately NOT granted.
    SERVICE_AGENT: [
        'ride:view', 'ride:cancel', 'account:view', 'support:view', 'support:respond', 'courier_job:view', 'courier_job:release',
        'support_ticket:view', 'support_ticket:manage', 'support_contact:reveal',
        'conversation:view', 'agent_desk:view', 'order:view', 'delivery:view',
        'conversation:manage', 'agent_desk:assign',
        // D632: the teams of its country (read) - which queues it works in; it never edits them.
        'central_team:view',
        // D419 review (W7C-02): the message text of its market's conversations — a purpose-bound read of Restricted-PII,
        // deliberately NOT a `view` action (so `*:view` never reaches it). It writes nothing.
        'conversation:read_body',
        // D473 (ADR-0091): the atendente READS a ride's chat and the central's orders at its market. It does NOT hold `ride_chat:send` /
        // `central_order:create`: the owner pinned its writes at the two desk words (D419, 2026-09-24 — "nothing more"), so speaking in a
        // ride and taking an order by phone stay with OPS, SUPPORT and the admins until the owner widens it (open owner question).
        'ride_chat:read', 'central_order:view',
    ],
};
/**
 * The scope kinds a role's grants may carry (D355, contract §4.1 of the wave-1 plan; PARTNER added by D365). A grant
 * whose scope kind is not listed for its role grants nothing — a GLOBAL partner agent or a PARTNER-scoped super admin
 * is a malformed seed, denied rather than trusted. Migrations m0110 + m0115 enforce the pairings at the database too.
 */
export const ROLE_SCOPE_TYPES = {
    SUPER_ADMIN: ['GLOBAL', 'COUNTRY', 'REGION', 'CITY'],
    COUNTRY_ADMIN: ['GLOBAL', 'COUNTRY', 'REGION', 'CITY'],
    CITY_ADMIN: ['GLOBAL', 'COUNTRY', 'REGION', 'CITY'],
    OPS: ['GLOBAL', 'COUNTRY', 'REGION', 'CITY'],
    SUPPORT: ['GLOBAL', 'COUNTRY', 'REGION', 'CITY'],
    SAFETY: ['GLOBAL', 'COUNTRY', 'REGION', 'CITY'],
    FINANCE: ['GLOBAL', 'COUNTRY', 'REGION', 'CITY'],
    MARKETING: ['GLOBAL', 'COUNTRY', 'REGION', 'CITY'],
    DRIVER_OPS: ['GLOBAL', 'COUNTRY', 'REGION', 'CITY'],
    MERCHANT_OPS: ['GLOBAL', 'COUNTRY', 'REGION', 'CITY'],
    ANALYST: ['GLOBAL', 'COUNTRY', 'REGION', 'CITY'],
    READ_ONLY: ['GLOBAL', 'COUNTRY', 'REGION', 'CITY'],
    MERCHANT_MANAGER: ['MERCHANT'],
    FLEET_MANAGER: ['FLEET'],
    PARTNER_AGENT: ['PARTNER'],
    SERVICE_AGENT: ['CITY', 'REGION', 'COUNTRY'],
};
/**
 * W8-OPERATORS-AND-SCOPES (D449, ADR-0078; finding A-05): the admin roles — each holds `*:*` bounded by its grant's scope.
 * Granting one is never one person's act: the console records a PENDING request that a DIFFERENT operator approves (never
 * the proposer, never the operator who receives the role), and the database refuses a console-made admin grant without that
 * approved request (m0146). Every other role is granted in one step, inside the granter's own scope and financial limit.
 */
export const FOUR_EYES_ADMIN_ROLES = ['SUPER_ADMIN', 'COUNTRY_ADMIN', 'CITY_ADMIN'];
/** True when granting `role` needs a second operator's approval (own-property safe: an unknown role is false). */
export function isFourEyesAdminRole(role) {
    return FOUR_EYES_ADMIN_ROLES.includes(role);
}
/**
 * The permissions a role grants, or an empty list for any unknown role (audit-auth-11; Codex Slice-2 round-2
 * #10). Uses an OWN-property lookup so an inherited `Object.prototype` key — `constructor`, `toString`,
 * `__proto__`, `hasOwnProperty`, `valueOf`, … — carried by a malformed grant row resolves to nothing (a clean
 * deny) rather than to `Object.prototype.constructor` (a function), which would make `permissionGranted` throw
 * a `TypeError` and 500 the request, bricking every legitimately co-granted action for that principal.
 */
export function permissionsForRole(role) {
    return Object.prototype.hasOwnProperty.call(ROLE_PERMISSIONS, role) ? ROLE_PERMISSIONS[role] : [];
}
/** True when a grant of `role` may carry `scopeType` (own-property lookup; an unknown role allows nothing). */
export function roleAllowsScopeType(role, scopeType) {
    if (!Object.prototype.hasOwnProperty.call(ROLE_SCOPE_TYPES, role))
        return false;
    return ROLE_SCOPE_TYPES[role].includes(scopeType);
}
/** True when `permission` is a well-formed `resource:action` with non-empty, non-wildcard parts. */
export function isValidPermission(permission) {
    const parts = permission.split(':');
    return parts.length === 2 && parts[0] !== '' && parts[1] !== '' && parts[0] !== '*' && parts[1] !== '*';
}
function matchOne(granted, required) {
    if (granted === '*:*')
        return true;
    const [gr, ga] = granted.split(':');
    const [rr, ra] = required.split(':');
    return (gr === '*' || gr === rr) && (ga === '*' || ga === ra);
}
/**
 * True when any granted permission (with wildcards) covers the required `resource:action` and no carve-out (`!resource:action`,
 * D854) of the same list refuses it. A carve-out entry never grants anything itself.
 */
export function permissionGranted(granted, required) {
    if (granted.includes(`!${required}`))
        return false;
    return granted.some((g) => !g.startsWith('!') && matchOne(g, required));
}
/** A privileged action is anything that is not a pure read. Privileged actions require MFA-level assurance. */
export function isPrivileged(permission) {
    const action = permission.split(':')[1] ?? '';
    return !READ_ACTIONS.includes(action);
}
//# sourceMappingURL=roles.js.map